import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import firebase from 'firebase/app'
import 'firebase/firestore'
import { auth } from '../firebase'
import { reactLocalStorage } from 'reactjs-localstorage'
import { NotificationManager } from 'react-notifications'
import {
  hasSessionExpired,
  INACTIVITY_CHECK_INTERVAL_MS,
  INACTIVITY_STORAGE_THROTTLE_MS,
  LAST_ACTIVITY_STORAGE_KEY,
  SESSION_EXPIRED_QUERY,
} from './inactivitySession'
import {
  activateWorkspace,
  listMyWorkspaces,
  workspaceDisplayName,
} from './workspaceService'

const AuthContext = React.createContext()

const BLOCKED_SCHOOL_STATUSES = ['blocked', 'inactive', 'deactivated', 'disabled']

const parseBoolLike = (v) => {
  if (v === true || v === 1) return true
  const s = String(v ?? '').trim().toLowerCase()
  if (!s) return false
  return ['true', '1', 'yes', 'on'].includes(s)
}

const isPlatformAdminSession = () => {
  const role = String(reactLocalStorage.get('role') || '').trim().toLowerCase()
  const flag = reactLocalStorage.get('platform-admin')
  if (parseBoolLike(flag)) return true
  return (
    role.includes('developer') ||
    role.includes('platform') ||
    role.includes('super')
  )
}

const isSchoolBlocked = (data = {}) => {
  const status = String(data.status || '').trim().toLowerCase()
  return (
    data.blocked === true ||
    data.active === false ||
    BLOCKED_SCHOOL_STATUSES.includes(status)
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState()
  const [loading, setLoading] = useState(true)

  // NEW: role + parent profile
  const [role, setRole] = useState(null) // 'PARENT' | null | later maybe 'STAFF', etc.
  const [parentProfile, setParentProfile] = useState(null)
  const schoolGuardUnsubRef = useRef(null)
  const forceLogoutRef = useRef(false)

  function signUp(email, password) {
    return auth.createUserWithEmailAndPassword(email, password)
  }

  function signIn(email, password) {
    return auth.signInWithEmailAndPassword(email, password)
  }

  // NOTE: these two don't really exist in client auth; leaving them as-is so we don't break anything.
  function disableUser(uid, status) {
    return auth.updateuser && auth.updateuser(uid, { disabled: status })
  }
  function resentVerification(email) {
    return auth.resentVerification && auth.resentVerification(email)
  }

  function resetPassword(email) {
    return auth.sendPasswordResetEmail(email)
  }

  function signOut() {
    return auth.signOut()
  }

  const clearSchoolGuard = useCallback(() => {
    if (typeof schoolGuardUnsubRef.current === 'function') {
      schoolGuardUnsubRef.current()
      schoolGuardUnsubRef.current = null
    }
  }, [])

  const forceLogoutBlockedSchool = useCallback(async (message) => {
    if (forceLogoutRef.current) return
    forceLogoutRef.current = true

    const note =
      String(message || '').trim() ||
      'This school has been blocked. Contact Edusaf support.'

    try {
      NotificationManager.error(note)
    } catch {}

    const blockedSchoolId = String(
      reactLocalStorage.get('current-school') || ''
    ).trim()
    try {
      const workspaces = await listMyWorkspaces()
      const fallback = workspaces.find(
        (workspace) =>
          workspace.available && workspace.schoolId !== blockedSchoolId
      )
      if (fallback) {
        try {
          NotificationManager.info(
            `Opening ${workspaceDisplayName(fallback)} so your other school access stays available.`
          )
        } catch {}
        await activateWorkspace({
          schoolId: fallback.schoolId,
          persona: fallback.defaultPersona,
        })
        return
      }
    } catch (workspaceError) {
      console.warn(
        '[AuthContext] unable to recover into another workspace:',
        workspaceError
      )
    }

    try {
      await auth.signOut()
    } catch {}

    try {
      reactLocalStorage.clear()
    } catch {}

    try {
      window.dispatchEvent(new Event('school-ctx-changed'))
    } catch {}

    if (typeof window !== 'undefined') {
      const atLogin = String(window.location.pathname || '').startsWith('/login')
      if (!atLogin) window.location.assign('/login')
    }
  }, [])

  const bindSchoolGuard = useCallback(() => {
    clearSchoolGuard()
    forceLogoutRef.current = false

    if (!auth.currentUser) return
    if (isPlatformAdminSession()) return

    const schoolId = String(reactLocalStorage.get('current-school') || '').trim()
    if (!schoolId) return

    const db = firebase.firestore()
    schoolGuardUnsubRef.current = db
      .collection('schools')
      .doc(schoolId)
      .onSnapshot(
        async (snap) => {
          if (!snap.exists) return
          const data = snap.data() || {}
          if (!isSchoolBlocked(data)) return
          await forceLogoutBlockedSchool(
            String(data.blockReason || '').trim() ||
              'This school has been blocked. Contact Edusaf support.'
          )
        },
        (err) => {
          console.error('[AuthContext] school guard failed:', err)
        }
      )
  }, [clearSchoolGuard, forceLogoutBlockedSchool])

  // ONLY RUN WHEN COMPONENT MOUNTS
 useEffect(() => {
  const db = firebase.firestore()

  const unSubscribe = auth.onAuthStateChanged(async (user) => {
    setCurrentUser(user)
    setParentProfile(null)
    setRole(null)

    if (!user) {
      setLoading(false)
      return
    }

    try {
      let claimRole = null
      try {
        const tokenResult = await user.getIdTokenResult()
        claimRole = tokenResult.claims.role || null
      } catch (err) {}

      const parentDoc = await db.collection('parents').doc(user.uid).get()

      if (parentDoc.exists) {
        setParentProfile({ id: parentDoc.id, ...parentDoc.data() })
        setRole(claimRole || 'PARENT')
      } else {
        setParentProfile(null)
        setRole(claimRole || null)
      }
    } catch (err) {
      console.error('[AuthContext] error loading extra auth info:', err)
    } finally {
      setLoading(false)
    }
  })

  return unSubscribe
}, [])

  useEffect(() => {
    if (!currentUser) {
      clearSchoolGuard()
      forceLogoutRef.current = false
      return
    }

    bindSchoolGuard()

    const onSchoolCtxChanged = () => bindSchoolGuard()
    const onStorage = (e) => {
      if (!e || !e.key) {
        bindSchoolGuard()
        return
      }
      if (e.key === 'current-school' || e.key === 'platform-admin' || e.key === 'role') {
        bindSchoolGuard()
      }
    }

    window.addEventListener('school-ctx-changed', onSchoolCtxChanged)
    window.addEventListener('storage', onStorage)

    return () => {
      window.removeEventListener('school-ctx-changed', onSchoolCtxChanged)
      window.removeEventListener('storage', onStorage)
      clearSchoolGuard()
    }
  }, [currentUser, bindSchoolGuard, clearSchoolGuard])

  useEffect(() => {
    if (!currentUser || typeof window === 'undefined') return undefined

    let lastActivityAt = Date.now()
    let lastStoredAt = 0
    let logoutStarted = false

    const persistActivity = (timestamp) => {
      try {
        window.localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp))
        lastStoredAt = timestamp
      } catch {}
    }

    const recordActivity = () => {
      const now = Date.now()
      lastActivityAt = now
      if (now - lastStoredAt >= INACTIVITY_STORAGE_THROTTLE_MS) {
        persistActivity(now)
      }
    }

    const expireSession = async () => {
      if (logoutStarted) return
      logoutStarted = true
      const loginPath = role === 'PARENT' ? '/parent/login' : '/login'

      try {
        await auth.signOut()
      } catch (err) {
        console.error('[AuthContext] inactivity signout failed:', err)
      }

      try {
        reactLocalStorage.clear()
      } catch {}

      window.location.replace(`${loginPath}?${SESSION_EXPIRED_QUERY}`)
    }

    const checkForExpiry = () => {
      if (hasSessionExpired(lastActivityAt)) expireSession()
    }

    const onStorage = (event) => {
      if (event.key !== LAST_ACTIVITY_STORAGE_KEY || !event.newValue) return
      const timestamp = Number(event.newValue)
      if (Number.isFinite(timestamp) && timestamp > lastActivityAt) {
        lastActivityAt = timestamp
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') recordActivity()
    }

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart']
    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, recordActivity, { passive: true })
    )
    window.addEventListener('storage', onStorage)
    document.addEventListener('visibilitychange', onVisibilityChange)
    persistActivity(lastActivityAt)

    const expiryInterval = window.setInterval(
      checkForExpiry,
      INACTIVITY_CHECK_INTERVAL_MS
    )

    return () => {
      window.clearInterval(expiryInterval)
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, recordActivity)
      )
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [currentUser, role])

  const value = {
    currentUser,
    role,
    parentProfile,
    isParent: role === 'PARENT',

    signUp,
    signIn,
    resetPassword,
    resentVerification,
    signOut,
    disableUser,
  }

  if (loading) {
    return (
      <div className="grid min-h-screen justify-center items-center">
        <i className={' text-primary fas fa-circle-notch fa-spin text-5xl'}></i>
      </div>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
